<template>
  <!--
    WHAT: Structured editor for opportunity_config.on_create_dossiers — which
          patient_dossier SATs attach to this metric's opportunities, what
          metadata key each result lands on, how it is refreshed, and how the
          SAT's run_context is built from the opportunity's own fields.
    WHERE: rendered inside OpportunityConfigPanel's "Opportunity Sync" card.
    CONTRACT:
      props:  modelValue (Array)  — the on_create_dossiers entries (may be [])
              dossierSats (Array) — [{id,name,config}] patient_dossier SATs
      emits:  update:modelValue   — on every edit (parent decides when to save)
    NOTES:
      - Emits the WHOLE array each time; the parent writes it onto cfg and lets
        its existing dirty/emit path handle persistence.
      - An entry with no sat_id or no store_key is dropped on emit — a
        half-filled row must never reach the backend, where a bad store_key
        produces a dossier nobody reads.
      - refresh is the product decision this widget exists for: "once" for a
        summary of a fixed past event (a discharge episode does not change),
        "daily" for one that decays (ED utilization: "days since last ED").
  -->
  <div>
    <div class="flex items-center justify-between mb-1.5">
      <label class="text-[11px] font-medium text-gray-500">Patient dossiers</label>
      <button
        class="text-[11px] text-blue-600 hover:text-blue-700"
        @click="addEntry"
      >＋ add dossier</button>
    </div>

    <p v-if="!entries.length" class="text-[11px] text-gray-400 italic">
      None. The opportunity carries no AI summary.
    </p>

    <div
      v-for="(e, i) in entries"
      :key="i"
      class="border border-gray-200 rounded p-2.5 mb-2 bg-gray-50/40"
    >
      <div class="flex items-center gap-2 mb-2">
        <select
          v-model.number="e.sat_id"
          class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 text-gray-800 focus:outline-none focus:border-blue-400"
          @change="onSatChange(e)"
        >
          <option :value="null">— select dossier —</option>
          <option v-for="s in dossierSats" :key="s.id" :value="s.id">
            {{ s.name }} ({{ s.id }})
          </option>
          <option
            v-if="e.sat_id && !dossierSats.some((s) => s.id === e.sat_id)"
            :value="e.sat_id"
          >#{{ e.sat_id }} (current)</option>
        </select>
        <button class="text-gray-400 hover:text-red-500 text-sm" @click="removeEntry(i)">&times;</button>
      </div>

      <div class="grid grid-cols-3 gap-2">
        <div>
          <label class="block text-[10px] text-gray-500 mb-0.5">Stored as</label>
          <input
            v-model.trim="e.store_key"
            type="text"
            placeholder="adt_assessment"
            class="text-xs px-2 py-1 border border-gray-200 rounded w-full font-mono text-gray-800 focus:outline-none focus:border-blue-400"
            @input="emit"
          />
        </div>
        <div>
          <label class="block text-[10px] text-gray-500 mb-0.5">Refresh</label>
          <select
            v-model="e.refresh"
            class="text-xs px-2 py-1 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400"
            @change="emit"
          >
            <option value="once">Once — build if missing</option>
            <option value="daily">Daily — rebuild when stale</option>
          </select>
        </div>
        <div v-if="e.refresh === 'daily'">
          <label class="block text-[10px] text-gray-500 mb-0.5">Stale after (hours)</label>
          <input
            v-model.number="e.refresh_after_hours"
            type="number"
            min="1"
            placeholder="20"
            class="text-xs px-2 py-1 border border-gray-200 rounded w-full font-mono text-gray-800 focus:outline-none focus:border-blue-400"
            @input="emit"
          />
        </div>
      </div>

      <!-- run_context: SAT filter key <- a whitelisted path on the opportunity.
           The backend only resolves metadata.<key> and a few plain opp fields,
           so free text here is safe: anything unrecognised is skipped, never
           evaluated. -->
      <div class="mt-2">
        <div class="flex items-center justify-between mb-1">
          <label class="text-[10px] text-gray-500">
            Run context — passes opportunity fields into the dossier query
          </label>
          <button class="text-[10px] text-blue-600 hover:text-blue-700" @click="addCtx(e)">＋ add</button>
        </div>
        <div v-for="(row, j) in e._ctx" :key="j" class="flex items-center gap-2 mb-1">
          <input
            v-model.trim="row.k"
            type="text"
            placeholder="discharge_time"
            class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
            @input="emit"
          />
          <span class="text-[10px] text-gray-400">←</span>
          <input
            v-model.trim="row.v"
            type="text"
            placeholder="metadata.discharge_time"
            class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
            @input="emit"
          />
          <button class="text-gray-400 hover:text-red-500 text-sm" @click="removeCtx(e, j)">&times;</button>
        </div>
        <p v-if="!e._ctx.length" class="text-[10px] text-gray-400 italic">
          None — the dossier runs against the whole patient.
        </p>
      </div>

      <p v-if="entryWarning(e)" class="text-[10px] text-amber-600 mt-1.5">
        {{ entryWarning(e) }}
      </p>
    </div>

    <p class="text-[10px] text-gray-400">
      Reconciled daily against every live opportunity — a missing dossier is retried,
      and a <span class="font-mono">daily</span> one is rebuilt once stale.
    </p>
  </div>
</template>

<script>
const DEFAULT_REFRESH_HOURS = 20;

export default {
  name: 'DossierBindingsEditor',
  props: {
    modelValue: { type: Array, default: () => [] },
    dossierSats: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return { entries: this.hydrate(this.modelValue) };
  },
  watch: {
    modelValue(v) {
      // Ignore the echo of our own emit; otherwise re-hydrating mid-edit would
      // rebuild _ctx rows and steal focus from the input being typed into.
      if (JSON.stringify(this.serialize()) === JSON.stringify(v || [])) return;
      this.entries = this.hydrate(v);
    },
  },
  methods: {
    hydrate(v) {
      return (Array.isArray(v) ? v : []).map((e) => ({
        sat_id: e.sat_id ?? null,
        store_key: e.store_key || '',
        refresh: e.refresh === 'daily' ? 'daily' : 'once',
        refresh_after_hours: e.refresh_after_hours ?? null,
        // run_context is an object on the wire, rows in the editor — an object
        // cannot preserve a half-typed key while the user is typing it.
        _ctx: Object.entries(e.run_context || {}).map(([k, val]) => ({ k, v: String(val) })),
      }));
    },
    serialize() {
      return this.entries
        .filter((e) => e.sat_id && e.store_key)
        .map((e) => {
          const out = {
            sat_id: e.sat_id,
            store_key: e.store_key,
            run_context: e._ctx.reduce((acc, r) => {
              if (r.k && r.v) acc[r.k] = r.v;
              return acc;
            }, {}),
          };
          // Only WRITE refresh when it is not the default. "once" is what the
          // backend infers from an absent key, so emitting it would rewrite every
          // existing binding on load and show a false "Unsaved" the moment anyone
          // opens the panel. Write only what the user actually chose.
          if (e.refresh === 'daily') {
            out.refresh = 'daily';
            if (e.refresh_after_hours) out.refresh_after_hours = Number(e.refresh_after_hours);
          }
          return out;
        });
    },
    emit() { this.$emit('update:modelValue', this.serialize()); },
    addEntry() {
      this.entries.push({
        sat_id: null, store_key: '', refresh: 'once',
        refresh_after_hours: null, _ctx: [],
      });
      this.emit();
    },
    removeEntry(i) { this.entries.splice(i, 1); this.emit(); },
    addCtx(e) { e._ctx.push({ k: '', v: '' }); this.emit(); },
    removeCtx(e, j) { e._ctx.splice(j, 1); this.emit(); },
    onSatChange(e) {
      // Default the store key to a slug of the SAT name so the common case needs
      // no typing; never overwrite one the user already set.
      if (!e.store_key) {
        const sat = this.dossierSats.find((s) => s.id === e.sat_id);
        if (sat && sat.name) {
          e.store_key = sat.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        }
      }
      this.emit();
    },
    entryWarning(e) {
      if (!e.sat_id) return 'Not saved until a dossier is selected.';
      if (!e.store_key) return 'Not saved until a stored-as key is set.';
      if (e.refresh === 'daily'
          && e.refresh_after_hours != null
          && Number(e.refresh_after_hours) < 1) {
        return `Stale-after must be at least 1 hour (blank uses ${DEFAULT_REFRESH_HOURS}).`;
      }
      return '';
    },
  },
};
</script>
